// src/App.tsx
import React from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";

import LandingPage from "./features/LandingPage";
import WODEditor from "./features/wod/pages/WODEditor";
import WODDisplay from "./features/wod/pages/WODDisplay";
import DipLeaderboard from "./features/leaderboard/pages/DipLeaderboard";
import Login from "./features/auth/pages/Login";
import Signup from "./features/auth/pages/Signup";
import Dashboard from "./features/dashboard/pages/Dashboard";
import Schedule from "./features/bookings/pages/Schedule";
import ClassRoster from "./features/bookings/pages/ClassRoster";
import Leaderboard from "./features/leaderboard/pages/Leaderboard";
import Training from "./features/training/pages/Training";
import TrainingCategory from "./features/training/pages/TrainingCategory";
import TrainingMovement from "./features/training/pages/TrainingMovement";
import Profile from "./features/profile/pages/Profile";
import { useAuth } from "./context/AuthContext";
import { isAdminRole, isMemberRole, isSuperadminRole } from "./context/authUser";
import { GymFeatureKey, isFeatureEnabled } from "./config/gymConfig";

import AdminInsights from "./features/admin/pages/AdminInsights";
import AdminPerformance from "./features/admin/pages/AdminPerformance";
import AdminMemberPerformance from "./features/admin/pages/AdminMemberPerformance";
import AdminMetricPerformance from "./features/admin/pages/AdminMetricPerformance";
import AdminMetricIndex from "./features/admin/pages/AdminMetricIndex";
import AdminMemberships from "./features/admin/pages/AdminMemberships";
import OpsStaffRoles from "./features/admin/pages/OpsStaffRoles";
import JoinLockerRoom from "./features/payments/pages/JoinLockerRoom";
import PayAsYouGo from "./features/payments/pages/PayAsYouGo";
import AdminTemplates from "./features/wod/pages/AdminTemplates";

/** ---------- Route guards ---------- */

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading)
    return <div className="app-loading text-center mt-20">Loading...</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  return children;
}

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { appUser, loading } = useAuth();
  const location = useLocation();

  if (loading)
    return <div className="app-loading text-center mt-20">Loading...</div>;
  if (!isAdminRole(appUser?.role))
    return <Navigate to="/dashboard" replace state={{ from: location }} />;

  return children;
}

function RequireSuperadmin({ children }: { children: React.ReactElement }) {
  const { appUser, loading } = useAuth();
  const location = useLocation();

  if (loading)
    return <div className="app-loading text-center mt-20">Loading...</div>;
  if (!isSuperadminRole(appUser?.role))
    return <Navigate to="/dashboard" replace state={{ from: location }} />;

  return children;
}

function RequireApproved({ children }: { children: React.ReactElement }) {
  const { user, appUser, loading } = useAuth();
  const location = useLocation();

  if (loading)
    return <div className="app-loading text-center mt-20">Loading...</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (appUser?.approvalStatus === "pending") {
    return <Navigate to="/join" replace state={{ from: location }} />;
  }

  return children;
}

function RequireActiveMembershipAccess({
  children,
}: {
  children: React.ReactElement;
}) {
  const { appUser, loading } = useAuth();
  const location = useLocation();
  const hasAccess =
    isAdminRole(appUser?.role) ||
    appUser?.appAccessStatus === "full" ||
    appUser?.appAccessStatus === "limited";

  if (loading) {
    return <div className="app-loading text-center mt-20">Loading...</div>;
  }

  if (!hasAccess) {
    return <Navigate to="/profile" replace state={{ from: location }} />;
  }

  return children;
}

function RequireMember({ children }: { children: React.ReactElement }) {
  const { appUser, loading } = useAuth();
  const location = useLocation();

  if (loading)
    return <div className="app-loading text-center mt-20">Loading...</div>;
  if (!isMemberRole(appUser?.role))
    return <Navigate to="/login" replace state={{ from: location }} />;

  return children;
}

function getAuthedHome(appUser: ReturnType<typeof useAuth>["appUser"]) {
  if (appUser?.approvalStatus === "pending") return "/join";
  if (!isAdminRole(appUser?.role) && appUser?.appAccessStatus === "none") return "/profile";
  return "/dashboard";
}

function RequireFeature({
  feature,
  children,
}: {
  feature: GymFeatureKey;
  children: React.ReactElement;
}) {
  if (!isFeatureEnabled(feature)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/** ---------- Layout ---------- */

function AdminLayout() {
  return (
    <div className="app-page min-h-screen overflow-x-hidden">
      <Outlet />
    </div>
  );
}

/** ---------- App ---------- */

export default function App() {
  const { user, appUser, loading } = useAuth();

  if (loading)
    return <div className="app-loading text-center mt-20">Loading...</div>;

  const isAuthed = !!user;

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/join" element={<JoinLockerRoom />} />
      <Route path="/payg" element={<PayAsYouGo />} />
      <Route path="/landing" element={<Navigate to="/" replace />} />

      <Route
        path="/login"
        element={isAuthed ? <Navigate to={getAuthedHome(appUser)} replace /> : <Login />}
      />
      <Route
        path="/signup"
        element={isAuthed ? <Navigate to={getAuthedHome(appUser)} replace /> : <Signup />}
      />
      <Route
        path="/pending-approval"
        element={
          <RequireAuth>
            <Navigate to="/join" replace />
          </RequireAuth>
        }
      />

      {/* Member routes */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <RequireApproved>
              <RequireActiveMembershipAccess>
                <RequireMember>
                  <Dashboard />
                </RequireMember>
              </RequireActiveMembershipAccess>
            </RequireApproved>
          </RequireAuth>
        }
      />

      <Route
        path="/schedule"
        element={
          <RequireAuth>
            <RequireApproved>
              <RequireActiveMembershipAccess>
                <RequireMember>
                  <Schedule />
                </RequireMember>
              </RequireActiveMembershipAccess>
            </RequireApproved>
          </RequireAuth>
        }
      />

      <Route
        path="/leaderboard"
        element={
          <RequireAuth>
            <RequireApproved>
              <RequireActiveMembershipAccess>
                <RequireFeature feature="leaderboard">
                  <RequireMember>
                    <Leaderboard />
                  </RequireMember>
                </RequireFeature>
              </RequireActiveMembershipAccess>
            </RequireApproved>
          </RequireAuth>
        }
      />

      <Route
        path="/board-of-shame"
        element={
          <RequireAuth>
            <RequireApproved>
              <RequireActiveMembershipAccess>
                <RequireFeature feature="boardOfShame">
                  <DipLeaderboard />
                </RequireFeature>
              </RequireActiveMembershipAccess>
            </RequireApproved>
          </RequireAuth>
        }
      />

      <Route
        path="/profile"
        element={
          <RequireAuth>
            <RequireApproved>
              <RequireFeature feature="profile">
                <Profile />
              </RequireFeature>
            </RequireApproved>
          </RequireAuth>
        }
      />

      <Route
        path="/training"
        element={
          <RequireFeature feature="trainingLogs">
            <RequireActiveMembershipAccess>
              <Training />
            </RequireActiveMembershipAccess>
          </RequireFeature>
        }
      />
      <Route
        path="/training/:category"
        element={
          <RequireFeature feature="trainingLogs">
            <RequireActiveMembershipAccess>
              <TrainingCategory />
            </RequireActiveMembershipAccess>
          </RequireFeature>
        }
      />
      <Route
        path="/training/:category/:movementSlug"
        element={
          <RequireFeature feature="trainingLogs">
            <RequireActiveMembershipAccess>
              <TrainingMovement />
            </RequireActiveMembershipAccess>
          </RequireFeature>
        }
      />

      {/* Admin-only area */}
      <Route
        element={
          <RequireAuth>
            <RequireApproved>
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            </RequireApproved>
          </RequireAuth>
        }
      >
        <Route
          path="/admin/insights"
          element={
            <RequireFeature feature="adminInsights">
              <AdminInsights />
            </RequireFeature>
          }
        />
        <Route
          path="/admin/performance"
          element={
            <RequireFeature feature="adminPerformance">
              <AdminPerformance />
            </RequireFeature>
          }
        />
        <Route
          path="/admin/performance/metrics"
          element={
            <RequireFeature feature="adminPerformance">
              <AdminMetricIndex />
            </RequireFeature>
          }
        />
        <Route
          path="/admin/performance/metric/:movementSlug/:metricType"
          element={
            <RequireFeature feature="adminPerformance">
              <AdminMetricPerformance />
            </RequireFeature>
          }
        />
        <Route
          path="/admin/performance/:userId"
          element={
            <RequireFeature feature="adminPerformance">
              <AdminMemberPerformance />
            </RequireFeature>
          }
        />
        <Route
          path="/display"
          element={
            <RequireFeature feature="ptProgramming">
              <WODDisplay />
            </RequireFeature>
          }
        />
        <Route
          path="/editor"
          element={
            <RequireFeature feature="ptProgramming">
              <WODEditor />
            </RequireFeature>
          }
        />
        <Route path="/admin/classes/:classId" element={<ClassRoster />} />
        <Route path="/admin/memberships" element={<Navigate to="/ops/memberships" replace />} />
        <Route path="/admin/timetable" element={<Navigate to="/ops/timetable" replace />} />
      </Route>

      <Route
        element={
          <RequireAuth>
            <RequireApproved>
              <RequireSuperadmin>
                <AdminLayout />
              </RequireSuperadmin>
            </RequireApproved>
          </RequireAuth>
        }
      >
        <Route path="/ops/staff" element={<OpsStaffRoles />} />
        <Route path="/ops/memberships" element={<AdminMemberships />} />
        <Route path="/ops/timetable" element={<AdminTemplates />} />
      </Route>

      {/* Catch-all */}
      <Route
        path="*"
        element={<Navigate to={isAuthed ? getAuthedHome(appUser) : "/"} replace />}
      />
    </Routes>
  );
}
